/** Shared impact-feedback option lists (keep in sync with submit-impact-feedback edge fn) */
export const IMPACT_AREAS = [
  'No known impact area',
  'Product / material',
  'Supplier service',
  'Kenvue site / plant',
  'Market / brand',
] as const

export const SUPPLY_STATUSES = [
  'No disruption expected',
  'Potential disruption',
  'Confirmed disruption',
] as const

export const IMPACT_DURATIONS = [
  'No duration impact',
  'Less than 7 days',
  '7 to 30 days',
  'More than 30 days',
] as const

export function formatImpactAreas(feedback: {
  impact_areas?: string[] | null
  impact_area?: string | null
}): string {
  if (Array.isArray(feedback.impact_areas) && feedback.impact_areas.length) {
    return feedback.impact_areas.join(', ')
  }
  return feedback.impact_area?.trim() || '—'
}

/** Toggle multi-select; "No known impact area" is exclusive. */
export function toggleImpactArea(current: string[], option: string): string[] {
  const noKnown = 'No known impact area'
  if (option === noKnown) {
    return current.includes(noKnown) ? [] : [noKnown]
  }
  const withoutNoKnown = current.filter((a) => a !== noKnown)
  if (withoutNoKnown.includes(option)) {
    return withoutNoKnown.filter((a) => a !== option)
  }
  return [...withoutNoKnown, option]
}

export type AlertSupplierLine = {
  supplierName: string
  uniqueId?: string
  category?: string
  subCategory?: string
  plant?: string
  brand?: string
  location?: string
  region?: string
  singleSource?: boolean | string
}

export type AlertPayload = {
  title?: string
  supplierName?: string
  suppliers?: AlertSupplierLine[]
  issueSummary?: string
  announcedDate?: string
  severity?: string
  uniqueId?: string
  location?: string
  region?: string
  category?: string
  subCategory?: string
  plant?: string
  brand?: string
  singleSource?: boolean | string
  immediateAction?: string
  eventOverview?: string
  overview?: string
  whyItMatters?: string
  nextBestAction?: string
  keyTakeaway?: string
  priceIncreases?: { productCategory?: string; tradeNames?: string; increase?: string }[]
  feedbackMode?: 'event' | 'per_supplier'
}

export type ImpactFeedback = {
  /** @deprecated prefer impact_areas */
  impact_area?: string | null
  impact_areas?: string[] | null
  supply_status: string
  impact_duration: string
  impact_details: string
  submitted_by_email?: string | null
  submitted_at?: string
  supplier_line_id?: string | null
}

export type AlertSupplierRow = {
  id: string
  sort_order?: number
  supplier_name: string
  unique_id?: string | null
  category?: string | null
  sub_category?: string | null
  plant?: string | null
  brand?: string | null
  location?: string | null
  region?: string | null
  single_source?: boolean | null
  line_status: 'pending' | 'received' | string
}

export function getCompanyLogoUrl(logoValue: string | undefined | null): string | null {
  if (!logoValue) return null
  if (logoValue.startsWith('http')) return logoValue
  if (logoValue.startsWith('logo')) return `/assets/${logoValue}`
  return null
}

export function singleSourceLabel(v: boolean | string | undefined | null): string {
  if (v === true || v === 'Yes' || v === 'yes' || v === 'true') return 'Yes'
  if (v === false || v === 'No' || v === 'no' || v === 'false') return 'No'
  if (v == null || v === '') return '—'
  return String(v)
}

export function listSuppliersFromPayload(p: AlertPayload): AlertSupplierLine[] {
  if (Array.isArray(p.suppliers) && p.suppliers.length) {
    return p.suppliers.filter((s) => String(s?.supplierName || '').trim())
  }
  if (String(p.supplierName || '').trim()) {
    return [
      {
        supplierName: String(p.supplierName).trim(),
        uniqueId: p.uniqueId,
        category: p.category,
        subCategory: p.subCategory,
        plant: p.plant,
        brand: p.brand,
        location: p.location,
        region: p.region,
        singleSource: p.singleSource,
      },
    ]
  }
  return []
}

function isSingleSourceTrue(v: boolean | string | undefined | null): boolean {
  return v === true || v === 'Yes' || v === 'yes' || v === 'true'
}

function allSuppliersSingleSource(p: AlertPayload): boolean {
  const lines = listSuppliersFromPayload(p)
  if (!lines.length) return isSingleSourceTrue(p.singleSource)
  return lines.every((s) => isSingleSourceTrue(s.singleSource ?? p.singleSource))
}

function categoryForEmail(p: AlertPayload): string {
  const lines = listSuppliersFromPayload(p)
  for (const s of lines) {
    const c = String(s.category || '').trim()
    if (c) return c
  }
  return String(p.category || '').trim()
}

function supplierLabelForEmail(p: AlertPayload): string {
  const lines = listSuppliersFromPayload(p)
  if (lines.length === 0) return 'Supplier'
  if (lines.length === 1) return lines[0].supplierName
  if (lines.length === 2) return `${lines[0].supplierName} & ${lines[1].supplierName}`
  return `${lines[0].supplierName} + ${lines.length - 1} more`
}

/** Matches Day-1 email headline shape (stored title preferred). */
export function alertHeadline(p: AlertPayload): string {
  if (p.title) return p.title
  const dateRaw = String(p.announcedDate || '').trim()
  const datePart = dateRaw
    ? dateRaw.toLowerCase().startsWith('announced ')
      ? dateRaw
      : `Announced ${dateRaw}`
    : ''
  return [
    supplierLabelForEmail(p),
    categoryForEmail(p),
    allSuppliersSingleSource(p) ? 'Single Source' : '',
    String(p.issueSummary || '').trim(),
    datePart,
  ]
    .filter(Boolean)
    .join(' | ')
}

export function emptySupplierDraft(): AlertSupplierLine & { singleSource: string } {
  return {
    supplierName: '',
    uniqueId: '',
    category: '',
    subCategory: '',
    plant: '',
    brand: '',
    singleSource: 'Yes',
  }
}
